/**
 * React Hook for Data Merging Operations
 * Integrates data merging logic with React Query and cache invalidation
 */

import { useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { 
  mergeTrackingEvents, 
  mergeCampaignStats, 
  mergePartialUpdate,
  createMergePerformanceMonitor,
  type MergeResult,
  type MergeOptions 
} from '../utils/dataMerging';
import type { 
  EnhancedMessageTrackingData, 
  CampaignStats 
} from '../services/outreach/types';
import { trackingKeys } from './queries/useTrackingData';
import { getCacheInvalidationManager } from '../utils/cacheInvalidation';

// Hook configuration
interface UseDataMergingOptions {
  /** Enable performance monitoring */
  enablePerformanceMonitoring?: boolean;
  /** Default merge options */
  defaultMergeOptions?: Partial<MergeOptions>;
  /** Callback for merge completion */
  onMergeComplete?: (result: MergeResult<any>) => void;
  /** Callback for merge errors */
  onMergeError?: (error: Error) => void;
}

/**
 * Custom hook for managing data merging operations
 * Provides utilities for merging tracking data, campaign stats, and partial updates
 */
export const useDataMerging = (options: UseDataMergingOptions = {}) => {
  const queryClient = useQueryClient();
  const performanceMonitor = useRef(createMergePerformanceMonitor());

  const {
    enablePerformanceMonitoring = false,
    defaultMergeOptions = {},
    onMergeComplete,
    onMergeError,
  } = options;

  /**
   * Merge new tracking data with existing cached data
   */
  const mergeTrackingData = useCallback(async (
    campaignId: string,
    newData: EnhancedMessageTrackingData[],
    mergeOptions?: Partial<MergeOptions>
  ): Promise<MergeResult<EnhancedMessageTrackingData[]>> => {
    try {
      const operation = enablePerformanceMonitoring 
        ? performanceMonitor.current.startOperation() 
        : null;

      // Get existing data from cache
      const queryKey = trackingKeys.campaign(campaignId);
      const existingData = queryClient.getQueryData<EnhancedMessageTrackingData[]>(queryKey) || [];

      if (operation) {
        operation.dataSize = existingData.length + newData.length;
      }

      // Perform merge
      const mergeResult = mergeTrackingEvents(
        existingData,
        newData,
        { ...defaultMergeOptions, ...mergeOptions }
      );

      // Update cache with merged data if there are changes
      if (mergeResult.hasChanges) {
        queryClient.setQueryData(queryKey, mergeResult.data);
        
        // Trigger cache invalidation manager update
        try {
          const cacheManager = getCacheInvalidationManager();
          cacheManager.invalidateByEvent('data_merged', campaignId);
        } catch (error) {
          console.warn('Failed to notify cache invalidation manager:', error);
        }
      }

      // Performance monitoring
      if (operation && enablePerformanceMonitoring) {
        const perf = performanceMonitor.current.endOperation(
          operation, 
          mergeResult.conflicts?.length || 0
        );
        console.debug(`Merge operation completed in ${perf.duration.toFixed(2)}ms`, perf.metrics);
      }

      onMergeComplete?.(mergeResult);
      return mergeResult;

    } catch (error) {
      const mergeError = error instanceof Error ? error : new Error('Unknown merge error');
      onMergeError?.(mergeError);
      throw mergeError;
    }
  }, [queryClient, defaultMergeOptions, enablePerformanceMonitoring, onMergeComplete, onMergeError]);

  /**
   * Merge campaign statistics
   */
  const mergeCampaignStatistics = useCallback(async (
    campaignId: string,
    newStats: CampaignStats,
    mergeOptions?: Partial<MergeOptions>
  ): Promise<MergeResult<CampaignStats>> => {
    try {
      const operation = enablePerformanceMonitoring 
        ? performanceMonitor.current.startOperation() 
        : null;

      // Get existing stats from cache (using a stats-specific query key)
      const queryKey = [...trackingKeys.campaign(campaignId), 'stats'];
      const existingStats = queryClient.getQueryData<CampaignStats>(queryKey);

      if (operation) {
        operation.dataSize = 1; // Single stats object
      }

      // Perform merge
      const mergeResult = mergeCampaignStats(
        existingStats || null,
        newStats,
        { ...defaultMergeOptions, ...mergeOptions }
      );

      // Update cache with merged data if there are changes
      if (mergeResult.hasChanges) {
        queryClient.setQueryData(queryKey, mergeResult.data);
      }

      // Performance monitoring
      if (operation && enablePerformanceMonitoring) {
        const perf = performanceMonitor.current.endOperation(
          operation, 
          mergeResult.conflicts?.length || 0
        );
        console.debug(`Stats merge completed in ${perf.duration.toFixed(2)}ms`, perf.metrics);
      }

      onMergeComplete?.(mergeResult);
      return mergeResult;

    } catch (error) {
      const mergeError = error instanceof Error ? error : new Error('Unknown stats merge error');
      onMergeError?.(mergeError);
      throw mergeError;
    }
  }, [queryClient, defaultMergeOptions, enablePerformanceMonitoring, onMergeComplete, onMergeError]);

  /**
   * Apply partial updates to tracking data
   */
  const applyPartialUpdates = useCallback(async <T extends Record<string, any>>(
    campaignId: string,
    updates: Array<{ id: string; updates: Partial<T> }>,
    idField: keyof T = 'id' as keyof T
  ): Promise<MergeResult<T[]>> => {
    try {
      const operation = enablePerformanceMonitoring 
        ? performanceMonitor.current.startOperation() 
        : null;

      // Get existing data from cache
      const queryKey = trackingKeys.campaign(campaignId);
      const existingData = queryClient.getQueryData<T[]>(queryKey) || [];

      if (operation) {
        operation.dataSize = existingData.length + updates.length;
      }

      // Perform partial merge
      const mergeResult = mergePartialUpdate(existingData, updates, idField);

      // Update cache with merged data if there are changes
      if (mergeResult.hasChanges) {
        queryClient.setQueryData(queryKey, mergeResult.data);
        
        // Trigger cache invalidation manager update
        try {
          const cacheManager = getCacheInvalidationManager();
          cacheManager.invalidateByEvent('partial_update', campaignId);
        } catch (error) {
          console.warn('Failed to notify cache invalidation manager:', error);
        }
      }

      // Performance monitoring
      if (operation && enablePerformanceMonitoring) {
        const perf = performanceMonitor.current.endOperation(operation, 0);
        console.debug(`Partial update completed in ${perf.duration.toFixed(2)}ms`, perf.metrics);
      }

      onMergeComplete?.(mergeResult);
      return mergeResult;

    } catch (error) {
      const mergeError = error instanceof Error ? error : new Error('Unknown partial update error');
      onMergeError?.(mergeError);
      throw mergeError;
    }
  }, [queryClient, enablePerformanceMonitoring, onMergeComplete, onMergeError]);

  /**
   * Batch merge multiple campaigns' data efficiently
   */
  const batchMergeTrackingData = useCallback(async (
    mergeOperations: Array<{
      campaignId: string;
      newData: EnhancedMessageTrackingData[];
      mergeOptions?: Partial<MergeOptions>;
    }>
  ): Promise<Array<{ campaignId: string; result: MergeResult<EnhancedMessageTrackingData[]> }>> => {
    const results: Array<{ campaignId: string; result: MergeResult<EnhancedMessageTrackingData[]> }> = [];

    try {
      const operation = enablePerformanceMonitoring 
        ? performanceMonitor.current.startOperation() 
        : null;

      if (operation) {
        operation.dataSize = mergeOperations.reduce((sum, op) => sum + op.newData.length, 0);
      }

      // Process all merge operations
      for (const { campaignId, newData, mergeOptions } of mergeOperations) {
        const result = await mergeTrackingData(campaignId, newData, mergeOptions);
        results.push({ campaignId, result });
      }

      // Performance monitoring
      if (operation && enablePerformanceMonitoring) {
        const totalConflicts = results.reduce((sum, r) => sum + (r.result.conflicts?.length || 0), 0);
        const perf = performanceMonitor.current.endOperation(operation, totalConflicts);
        console.debug(`Batch merge completed in ${perf.duration.toFixed(2)}ms`, perf.metrics);
      }

      return results;

    } catch (error) {
      const mergeError = error instanceof Error ? error : new Error('Unknown batch merge error');
      onMergeError?.(mergeError);
      throw mergeError;
    }
  }, [mergeTrackingData, enablePerformanceMonitoring, onMergeError]);

  /**
   * Smart merge that automatically detects the best merge strategy
   */
  const smartMerge = useCallback(async (
    campaignId: string,
    newData: EnhancedMessageTrackingData[]
  ): Promise<MergeResult<EnhancedMessageTrackingData[]>> => {
    const existingData = queryClient.getQueryData<EnhancedMessageTrackingData[]>(
      trackingKeys.campaign(campaignId)
    ) || [];

    // Determine optimal merge strategy based on data characteristics
    const mergeOptions: Partial<MergeOptions> = {
      timestampStrategy: 'latest',
      validateIntegrity: true,
      deepMerge: true,
    };

    // If existing data is much larger than new data, use preserve_existing for timestamps
    if (existingData.length > newData.length * 3) {
      mergeOptions.timestampStrategy = 'preserve_existing';
    }

    // If new data is significantly newer (all timestamps are recent), prioritize new data
    const now = Date.now();
    const recentThreshold = 5 * 60 * 1000; // 5 minutes
    const allNewDataRecent = newData.every(item => 
      now - new Date(item.timestamp).getTime() < recentThreshold
    );

    if (allNewDataRecent && newData.length > 0) {
      mergeOptions.timestampStrategy = 'latest';
      mergeOptions.conflictResolver = (field, oldValue, newValue) => {
        // For recent data, prefer new values for status and tracking fields
        if (['status', 'openedAt', 'clickedAt', 'deliveredAt'].includes(field)) {
          return newValue;
        }
        return newValue; // Default to new value for recent data
      };
    }

    return mergeTrackingData(campaignId, newData, mergeOptions);
  }, [queryClient, mergeTrackingData]);

  /**
   * Get performance metrics
   */
  const getPerformanceMetrics = useCallback(() => {
    return performanceMonitor.current.getMetrics();
  }, []);

  /**
   * Reset performance metrics
   */
  const resetPerformanceMetrics = useCallback(() => {
    performanceMonitor.current.reset();
  }, []);

  /**
   * Memoized utilities object
   */
  const utilities = useMemo(() => ({
    mergeTrackingData,
    mergeCampaignStatistics,
    applyPartialUpdates,
    batchMergeTrackingData,
    smartMerge,
    getPerformanceMetrics,
    resetPerformanceMetrics,
  }), [
    mergeTrackingData,
    mergeCampaignStatistics,
    applyPartialUpdates,
    batchMergeTrackingData,
    smartMerge,
    getPerformanceMetrics,
    resetPerformanceMetrics,
  ]);

  return utilities;
};

/**
 * Hook for monitoring merge operations across the application
 */
export const useMergeMonitoring = () => {
  const performanceMonitor = useRef(createMergePerformanceMonitor());

  const getGlobalMetrics = useCallback(() => {
    return performanceMonitor.current.getMetrics();
  }, []);

  const resetGlobalMetrics = useCallback(() => {
    performanceMonitor.current.reset();
  }, []);

  return {
    getGlobalMetrics,
    resetGlobalMetrics,
  };
};